/**
 * Canonical course-URL construction for the runtime + theme layer.
 *
 * <p>The portal mirrors the legacy akademi-lms-v2 URL scheme with one
 * difference (course URLs live under the `/lxp` namespace):
 * <ul>
 *   <li>course DETAIL  → <b>singular</b> `/lxp/course/{title-id}`
 *       (legacy `/course/{title-id}`)</li>
 *   <li>my-courses HUB → <b>plural</b> `/lxp/courses` + `/lxp/courses/{view}`
 *       (legacy `/courses`, `/courses/{view}`)</li>
 * </ul>
 * The singular-vs-plural split is exactly how legacy avoids a collision
 * between a course id and a view slug — so we keep it.
 *
 * <p>The detail slug is the legacy "friendly" form: a slugified title with
 * the numeric id appended (`intro-to-react-198109`). The detail route then
 * recovers the id from the trailing `-{id}` (see `getIdFromFriendlyPath`).
 * Title is optional — an id-only slug still round-trips.
 */

/** Base path for the course DETAIL route (singular). */
export const COURSE_DETAIL_BASE = '/lxp/course';

/** Base path for the my-courses HUB + its view paths (plural). */
export const MY_COURSES_BASE = '/lxp/courses';

/** Base path for the course catalog / full search-results page. */
export const CATALOG_BASE = '/lxp/catalog';

/**
 * Catalog URL carrying a free-text search query, e.g.
 * {@code catalogSearchHref('a1') → '/lxp/catalog?sort=popular&q=a1'}.
 *
 * <p>`base` may itself contain a query string (an operator-configured
 * catalog href); the `q` param is merged in with the correct separator
 * so existing params (like `sort=popular`) are preserved rather than
 * clobbered. Used by the header search box for the "see all results" /
 * Enter action.
 */
export function catalogSearchHref(
  query: string,
  base: string = `${CATALOG_BASE}?sort=popular`,
): string {
  const q = (query ?? '').trim();
  if (!q) return base;
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}q=${encodeURIComponent(q)}`;
}

/**
 * Build the legacy `title-id` friendly slug, e.g.
 * {@code toFriendlyCourseSlug('Intro to React', 198109) → 'intro-to-react-198109'}.
 * Mirrors the app-side `toFriendlyPath` (kept in sync; this copy lives in
 * the runtime so theme packages — which cannot import the portal app — can
 * build the same URL).
 */
export function toFriendlyCourseSlug(
  title: string | undefined | null,
  id: number | string,
): string {
  const normalized = (title ?? '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${normalized || 'course'}-${id}`;
}

/**
 * Full course-detail href: {@code /lxp/course/{title-id}}. `base` lets an
 * operator/theme override the namespace while keeping the friendly slug.
 */
export function courseDetailHref(
  title: string | undefined | null,
  id: number | string,
  base: string = COURSE_DETAIL_BASE,
): string {
  return `${base.replace(/\/$/, '')}/${toFriendlyCourseSlug(title, id)}`;
}
